// Landing Page - Public landing for unauthenticated users
// Design inspired by AWS Startups - gradient-forward with pink/purple/cyan accents
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Cpu, Wrench, Zap, ChevronDown, ChevronUp, ChevronRight,
  MessageSquare, Package, Rocket, Clock, DollarSign, Server, Cloud,
  ShoppingCart, Calendar, Bot, Cog, CircuitBoard, Wifi, Battery,
  Eye, Hand, Brain, CheckCircle, HelpCircle, Users, Shield, Lightbulb, Sparkles
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import { useAuth } from '../contexts/AuthContext';
import {
  trackLandingPageView,
  trackGetStartedClick,
  trackFaqExpand,
  trackExampleAnimationView
} from '../services/analytics';

// FAQ data for non-tech users
const FAQ_DATA = [
  {
    question: "I'm not a technical person. Is VinciKit for me?",
    answer: "Absolutely! VinciKit is designed specifically for people who have great ideas but aren't sure how to bring them to life technically. You describe what you want in plain English - like 'I want to build a robot that waters my plants' - and we figure out all the technical pieces you'll need. No coding knowledge required to get started."
  },
  {
    question: "What exactly do I get from VinciKit?",
    answer: "You get a complete blueprint for your project including: a timeline with milestones, estimated costs broken down by component, a list of parts and where to buy them (for hardware projects), recommended cloud services and APIs, server setup guides, and step-by-step instructions. Think of it as having a technical co-founder who plans everything for you."
  },
  {
    question: "How is this different from just asking ChatGPT?",
    answer: "ChatGPT can give you general advice, but VinciKit gives you an actionable plan tailored to YOUR specific situation - your budget, your technical skills, your timeline, and your existing tools. We ask the right questions to understand your context, then recommend specific products, services, and configurations that work together. It's the difference between 'you could use a database' and 'use Supabase's free tier, here's exactly how to set it up for your use case.'"
  },
  {
    question: "Do I need to know how to code?",
    answer: "Not necessarily! Many projects can be built using no-code tools like Zapier, Make, or Bubble. VinciKit will recommend the right approach based on your skills. If your project does require coding, we'll tell you exactly what skills are needed and can recommend learning resources or freelancers who can help."
  },
  {
    question: "How much does VinciKit cost?",
    answer: "VinciKit itself has a free tier for basic projects. We'll also show you the estimated costs for the tools, services, and parts in your recommended stack - this varies based on your project. A simple automation might cost $0-20/month, while a complex hardware project could require a larger upfront investment. We always show costs upfront so there are no surprises."
  },
  {
    question: "What if I want to change something in my plan?",
    answer: "Your plan is fully customizable! If you want to swap a recommended tool for something else, adjust your budget, or change your timeline, you can regenerate your stack with new preferences. VinciKit adapts to your needs."
  },
  {
    question: "Can VinciKit help with hardware projects like robots or IoT devices?",
    answer: "Yes! VinciKit excels at hardware projects. We'll recommend specific components (motors, sensors, microcontrollers), where to buy them, estimated shipping times, and how they connect together. We also cover the software side - the cloud services needed to control your device remotely, store data, and more."
  },
  {
    question: "What's an MCP and do I need one?",
    answer: "MCP (Model Context Protocol) is a way for AI assistants like ChatGPT to connect directly to tools and services. If you're using ChatGPT, you can use VinciKit's MCP to get personalized tool recommendations right in your chat. It's optional - you can use VinciKit's website directly if you prefer."
  }
];

// Animated flow steps for the robot example
const ROBOT_FLOW_STEPS = [
  {
    type: 'user',
    icon: MessageSquare,
    label: 'Your Idea',
    content: '"I want to build an android/robot like the I, Robot movie with Will Smith, but to be able to control it remotely"',
    delay: 0
  },
  {
    type: 'questions',
    icon: HelpCircle,
    label: 'We Ask',
    content: [
      'What\'s your budget range?',
      'Do you have any electronics experience?',
      'Indoor or outdoor use?',
      'What tasks should it perform?'
    ],
    delay: 0.5
  },
  {
    type: 'analysis',
    icon: Brain,
    label: 'We Analyze',
    content: 'Matching your needs with 500+ tools, APIs, and components...',
    delay: 1
  }
];

// Stack output for the robot example
const ROBOT_STACK = {
  hardware: [
    { name: 'Raspberry Pi 5', desc: 'Main brain - runs AI locally', cost: '$80' },
    { name: 'Arduino Mega', desc: 'Motor & sensor control', cost: '$45' },
    { name: 'Servo Motors (x12)', desc: 'Joint movement', cost: '$180' },
    { name: 'Ultrasonic Sensors', desc: 'Obstacle detection', cost: '$25' },
    { name: 'Camera Module', desc: 'Computer vision', cost: '$35' },
    { name: '3D Printed Frame', desc: 'Custom body parts', cost: '$150' }
  ],
  software: [
    { name: 'Claude AI API', desc: 'Natural language processing', cost: '$20/mo' },
    { name: 'Firebase', desc: 'Real-time remote control', cost: 'Free tier' },
    { name: 'TensorFlow Lite', desc: 'On-device object recognition', cost: 'Free' }
  ],
  cloud: [
    { name: 'Google Cloud IoT', desc: 'Device management & telemetry', cost: '$10/mo' },
    { name: 'Twilio', desc: 'Voice commands via phone', cost: '$5/mo' }
  ],
  timeline: [
    { phase: 'Phase 1: Frame & Motors', duration: '2-3 weeks' },
    { phase: 'Phase 2: Electronics & Wiring', duration: '2 weeks' },
    { phase: 'Phase 3: Basic Movement Code', duration: '1-2 weeks' },
    { phase: 'Phase 4: Remote Control App', duration: '1 week' },
    { phase: 'Phase 5: AI Integration', duration: '2-3 weeks' }
  ],
  totalCost: '$515 upfront + ~$35/month',
  suppliers: ['Adafruit', 'SparkFun', 'Amazon', 'AliExpress']
};

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [showStack, setShowStack] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isAnimating, setIsAnimating] = useState(true);

  // Redirect if already logged in (only after auth is loaded)
  useEffect(() => {
    if (!loading && user) {
      navigate('/home');
    }
  }, [user, loading, navigate]);

  // Track landing page view
  useEffect(() => {
    trackLandingPageView();
  }, []);

  // Animate through the flow steps
  useEffect(() => {
    if (!isAnimating) return;

    const stepInterval = setInterval(() => {
      setActiveStep(prev => {
        if (prev < ROBOT_FLOW_STEPS.length - 1) {
          return prev + 1;
        } else {
          setShowStack(true);
          setIsAnimating(false);
          return prev;
        }
      });
    }, 2000);

    return () => clearInterval(stepInterval);
  }, [isAnimating]);

  // Restart animation
  const restartAnimation = () => {
    setActiveStep(0);
    setShowStack(false);
    setIsAnimating(true);
  };

  const handleGetStarted = (source = 'hero') => {
    trackGetStartedClick(source);
    navigate('/login');
  };

  const handleFaqToggle = (index) => {
    if (expandedFaq !== index) {
      trackFaqExpand(index, FAQ_DATA[index].question);
    }
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#0d1117] text-white">
      {/* Gradient background overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative px-6 py-4 sticky top-0 bg-[#0d1117]/80 backdrop-blur-md z-50 border-b border-white/10">
        <nav className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
              <GoldenSpiral className="w-6 h-6" color="white" />
            </div>
            <span className="font-bold text-xl tracking-tight">VinciKit</span>
          </div>
          <div className="flex items-center gap-8">
            <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors hidden sm:block text-sm font-medium">
              How it works
            </a>
            <a href="#example" className="text-gray-400 hover:text-white transition-colors hidden sm:block text-sm font-medium">
              Example
            </a>
            <a href="#faq" className="text-gray-400 hover:text-white transition-colors hidden sm:block text-sm font-medium">
              FAQ
            </a>
            <button
              onClick={() => handleGetStarted('header')}
              className="px-5 py-2.5 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity text-sm"
            >
              Get Started
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="relative px-6 pt-20 pb-32">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm font-medium mb-8">
              <span className="w-2 h-2 bg-gradient-to-r from-pink-500 to-cyan-500 rounded-full animate-pulse" />
              The inventor's toolkit for AI-powered ideas
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-8 leading-[1.1] tracking-tight">
              Turn your idea into{' '}
              <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                a complete project plan
              </span>
            </h1>

            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Want to build a robot? An AI app? Automate your business?
              <span className="text-white font-medium"> We'll tell you exactly what you need</span> -
              timeline, costs, parts, cloud services, and step-by-step guidance.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => handleGetStarted('hero')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white font-semibold rounded-xl
                         hover:opacity-90 transition-opacity text-lg"
              >
                Start Building
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => document.getElementById('example')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/20 text-white font-semibold rounded-xl
                         hover:bg-white/10 transition-colors text-lg"
              >
                See Example
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-6">
              Free to try - No credit card required
            </p>
          </motion.div>
        </div>

        {/* What You Get Section */}
        <section className="max-w-6xl mx-auto mt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 tracking-tight">
              Your Complete Project Blueprint
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto text-lg">
              VinciKit doesn't just give advice - we give you everything you need to actually build your project
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Calendar className="w-6 h-6" />,
                title: "Timeline & Milestones",
                description: "Step-by-step phases with realistic timeframes. Know exactly what to do first, second, and third.",
                gradient: "from-pink-500 to-rose-500"
              },
              {
                icon: <DollarSign className="w-6 h-6" />,
                title: "Cost Breakdown",
                description: "Upfront costs, monthly fees, and where your money goes. No surprises, just clarity.",
                gradient: "from-purple-500 to-violet-500"
              },
              {
                icon: <ShoppingCart className="w-6 h-6" />,
                title: "Parts & Suppliers",
                description: "For hardware projects: exact components, where to buy them, and estimated shipping times.",
                gradient: "from-cyan-500 to-blue-500"
              },
              {
                icon: <Server className="w-6 h-6" />,
                title: "Server Setup",
                description: "Which hosting to use, how to configure it, and what it'll cost based on your scale.",
                gradient: "from-pink-500 to-purple-500"
              },
              {
                icon: <Cloud className="w-6 h-6" />,
                title: "Cloud Services",
                description: "AI APIs, databases, authentication, storage - all configured for your specific needs.",
                gradient: "from-purple-500 to-cyan-500"
              },
              {
                icon: <Wrench className="w-6 h-6" />,
                title: "Integration Guides",
                description: "How everything connects together, with specific configurations and best practices.",
                gradient: "from-cyan-500 to-teal-500"
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 hover:bg-white/10 transition-colors group"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  {item.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="max-w-6xl mx-auto mt-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 tracking-tight">How It Works</h2>
            <p className="text-gray-400 max-w-xl mx-auto text-lg">
              From idea to actionable plan in about 2 minutes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Sparkles className="w-6 h-6" />,
                step: "01",
                title: "Describe Your Idea",
                description: "Tell us what you want to build in plain English. \"I want to make a robot that can fetch things\" works perfectly.",
                gradient: "from-pink-500 to-rose-500"
              },
              {
                icon: <Cpu className="w-6 h-6" />,
                step: "02",
                title: "Answer Quick Questions",
                description: "We'll ask about your budget, skills, and timeline. This helps us tailor recommendations to what's realistic for you.",
                gradient: "from-purple-500 to-violet-500"
              },
              {
                icon: <Package className="w-6 h-6" />,
                step: "03",
                title: "Get Your Blueprint",
                description: "Receive a complete plan with timeline, costs, parts list, cloud services, and step-by-step guidance.",
                gradient: "from-cyan-500 to-blue-500"
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="relative bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 text-center"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className={`inline-block px-3 py-1 bg-gradient-to-r ${step.gradient} rounded-full text-xs font-bold`}>
                    {step.step}
                  </span>
                </div>
                <div className={`w-14 h-14 bg-gradient-to-br ${step.gradient} rounded-xl flex items-center justify-center mx-auto mb-5`}>
                  {step.icon}
                </div>
                <h3 className="font-semibold text-xl mb-3">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Animated Robot Example */}
        <section id="example" className="max-w-5xl mx-auto mt-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4 tracking-tight">See It In Action</h2>
            <p className="text-gray-400 max-w-xl mx-auto text-lg">
              Watch how VinciKit transforms "I want to build a robot" into a complete project plan
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-pink-500/10 via-purple-500/10 to-cyan-500/10 rounded-3xl p-8 border border-white/10 backdrop-blur-sm"
          >
            {/* Flow Animation */}
            <div className="space-y-6 mb-8">
              {ROBOT_FLOW_STEPS.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{
                    opacity: activeStep >= index ? 1 : 0.3,
                    x: activeStep >= index ? 0 : -20
                  }}
                  transition={{ duration: 0.5, delay: step.delay }}
                  className={`bg-white/5 backdrop-blur-sm rounded-xl p-5 border ${activeStep >= index ? 'border-purple-500/50' : 'border-white/5'}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      step.type === 'user' ? 'bg-gradient-to-br from-pink-500 to-rose-500' :
                      step.type === 'questions' ? 'bg-gradient-to-br from-purple-500 to-violet-500' :
                      'bg-gradient-to-br from-cyan-500 to-blue-500'
                    }`}>
                      <step.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <span className={`text-sm font-semibold ${
                        step.type === 'user' ? 'text-pink-400' :
                        step.type === 'questions' ? 'text-purple-400' :
                        'text-cyan-400'
                      }`}>{step.label}</span>
                      {Array.isArray(step.content) ? (
                        <ul className="mt-2 space-y-1">
                          {step.content.map((q, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: activeStep >= index ? 1 : 0 }}
                              transition={{ delay: 0.1 * i }}
                              className="text-sm text-gray-400 flex items-center gap-2"
                            >
                              <ChevronRight className="w-3 h-3 text-purple-400" />
                              {q}
                            </motion.li>
                          ))}
                        </ul>
                      ) : (
                        <p className={`mt-1 ${step.type === 'user' ? 'text-gray-200 font-medium italic' : 'text-gray-400'}`}>
                          {step.content}
                        </p>
                      )}
                    </div>
                    {activeStep >= index && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Stack Output */}
            <AnimatePresence>
              {showStack && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-green-500/30"
                >
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-green-400">Your Complete Project Blueprint</span>
                      <p className="text-xs text-gray-500">Everything you need to build your robot</p>
                    </div>
                  </div>

                  {/* Hardware */}
                  <div className="mb-6">
                    <h4 className="flex items-center gap-2 text-sm font-semibold mb-3">
                      <CircuitBoard className="w-4 h-4 text-orange-400" />
                      Hardware Components
                    </h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {ROBOT_STACK.hardware.map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 * i }}
                          className="bg-orange-500/10 rounded-lg p-3 border border-orange-500/20"
                        >
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-sm">{item.name}</span>
                            <span className="text-xs text-orange-400 font-semibold">{item.cost}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Software & Cloud */}
                  <div className="grid sm:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold mb-3">
                        <Cpu className="w-4 h-4 text-blue-400" />
                        Software & AI
                      </h4>
                      <div className="space-y-2">
                        {ROBOT_STACK.software.map((item, i) => (
                          <div key={i} className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                            <div className="flex justify-between items-start">
                              <span className="font-medium text-sm">{item.name}</span>
                              <span className="text-xs text-blue-400 font-semibold">{item.cost}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-semibold mb-3">
                        <Cloud className="w-4 h-4 text-purple-400" />
                        Cloud Services
                      </h4>
                      <div className="space-y-2">
                        {ROBOT_STACK.cloud.map((item, i) => (
                          <div key={i} className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                            <div className="flex justify-between items-start">
                              <span className="font-medium text-sm">{item.name}</span>
                              <span className="text-xs text-purple-400 font-semibold">{item.cost}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="mb-6">
                    <h4 className="flex items-center gap-2 text-sm font-semibold mb-3">
                      <Calendar className="w-4 h-4 text-green-400" />
                      Project Timeline
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {ROBOT_STACK.timeline.map((phase, i) => (
                        <div key={i} className="bg-green-500/10 rounded-lg px-3 py-2 border border-green-500/20">
                          <span className="text-xs font-medium text-green-400">{phase.phase}</span>
                          <span className="text-xs text-gray-500 ml-2">{phase.duration}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-6">
                      <div>
                        <span className="text-sm text-gray-500">Total Estimated Cost</span>
                        <p className="font-semibold">{ROBOT_STACK.totalCost}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Where to Buy</span>
                        <p className="font-medium text-gray-300">{ROBOT_STACK.suppliers.join(', ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">Ready to start building!</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Replay button */}
            {showStack && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={restartAnimation}
                className="mt-4 text-sm text-purple-400 hover:text-purple-300 font-medium flex items-center gap-1 mx-auto"
              >
                <Zap className="w-4 h-4" />
                Replay animation
              </motion.button>
            )}
          </motion.div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="max-w-3xl mx-auto mt-40">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4 tracking-tight">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-lg">
              Got questions? We've got answers - especially for non-technical folks.
            </p>
          </motion.div>

          <div className="space-y-3">
            {FAQ_DATA.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
              >
                <button
                  onClick={() => handleFaqToggle(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-5 text-gray-400 leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto mt-40 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-cyan-500/20 rounded-3xl p-12 border border-white/10 backdrop-blur-sm relative overflow-hidden"
          >
            {/* Gradient orbs */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500/30 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/30 rounded-full blur-3xl" />

            <div className="relative">
              <h2 className="text-4xl font-bold mb-4 tracking-tight">Ready to Build Something Amazing?</h2>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto text-lg">
                Stop researching and start building. Get your complete project blueprint in 2 minutes.
              </p>
              <button
                onClick={() => handleGetStarted('cta_section')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white font-semibold rounded-xl
                         hover:opacity-90 transition-opacity text-lg"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </section>

        {/* Social Proof */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto mt-16 text-center"
        >
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-medium">
              <Zap className="w-4 h-4" />
              Powered by Claude AI
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium">
              <Package className="w-4 h-4" />
              500+ tools and APIs
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-sm font-medium">
              <Users className="w-4 h-4" />
              ChatGPT MCP available
            </div>
          </div>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="relative px-6 py-10 border-t border-white/10 bg-[#0d1117]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-semibold">VinciKit</span>
          </div>
          <div className="flex items-center gap-8">
            <a href="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">Privacy</a>
            <a href="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">Terms</a>
            <a href="/pricing" className="text-sm text-gray-500 hover:text-white transition-colors">Pricing</a>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 VinciKit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
